import { Button } from '@playerbase/ui';

import {
  type BuilderFieldKind,
  FIELD_CAP,
} from '../../../games/use-form-builder-state.js';

interface FieldSidePanelProps {
  fieldCount: number;
  hasNps: boolean;
  onAdd: (kind: BuilderFieldKind) => void;
}

const KIND_OPTIONS: Array<{ kind: BuilderFieldKind; label: string; description: string }> = [
  { kind: 'short_text', label: 'Short text', description: 'One-line answer' },
  { kind: 'long_text', label: 'Long text', description: 'Multi-line paragraph' },
  { kind: 'rating', label: 'Rating', description: '1 to 5 or 1 to 10' },
  { kind: 'select', label: 'Select', description: 'Pick from a list (single or multi)' },
  { kind: 'severity', label: 'Severity', description: 'Low / Medium / High / Critical' },
  { kind: 'binary', label: 'Binary', description: 'Yes / No' },
  { kind: 'nps', label: 'NPS', description: 'Net Promoter Score - 1 to 10' },
];

// Vertical list of buttons rather than draggable chips. Drag-to-add is
// listed as a future polish item in the Notion story; click-to-append is
// the implementation behaviour for PR 3.
export function FieldSidePanel({
  fieldCount,
  hasNps,
  onAdd,
}: FieldSidePanelProps): JSX.Element {
  const atCap = fieldCount >= FIELD_CAP;
  return (
    <aside className="flex flex-col gap-3 rounded-md border border-border bg-surface-3 p-4 md:sticky md:top-4 md:self-start md:max-h-[calc(100vh-2rem)] md:overflow-y-auto">
      <header className="flex flex-col gap-1">
        <h3 className="font-display text-sm font-semibold text-text">Add a field</h3>
        <p className="text-xs text-text-muted">
          {atCap
            ? `Maximum ${FIELD_CAP} fields per form.`
            : `${fieldCount} of ${FIELD_CAP} used.`}
        </p>
      </header>
      <ul className="flex flex-col gap-2">
        {KIND_OPTIONS.map((option) => {
          // NPS is fixed-shape and only meaningful once per form; the
          // RPC enforces the same rule server-side.
          const npsBlocked = option.kind === 'nps' && hasNps;
          const disabled = atCap || npsBlocked;
          const tooltip = atCap
            ? 'Remove a field to add another.'
            : npsBlocked
              ? 'Only one NPS field per form.'
              : `Add ${option.label} - ${option.description}`;
          return (
            <li key={option.kind}>
              <Button
                variant="ghost"
                type="button"
                onClick={() => onAdd(option.kind)}
                disabled={disabled}
                // Description carried in the title attribute as a tooltip
                // hint - the button itself only shows the label + the "+"
                // affordance so the row never overflows on narrow rails.
                title={tooltip}
                className="flex w-full items-center justify-between gap-2"
              >
                <span className="text-sm text-text">{option.label}</span>
                <span aria-hidden className="text-base leading-none text-text-muted">
                  +
                </span>
              </Button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
